import React, { createContext, Dispatch, useContext, useReducer } from 'react';
import { ChildEventProps, LocalizedDateTime } from '../../types';
import { EventAction } from './actions';
import { eventReducer } from './reducers';
import { getEventBasicInfo } from './utils';

export interface EventBasicInformation {
    id: string;
    name: string;
    summary: string;
    url?: string;
    start?: LocalizedDateTime;
    end?: LocalizedDateTime;
    place: {
        isOnline?: boolean;
        latitude?: number;
        longitude?: number;
        placeId?: string;
    };
    childEvents: Array<ChildEventProps> | null;
    isSeriesParent: boolean;
    salesStatus: string;
}

export const useEventBasicInformationContext = () => {
    const value = useContext(EventBasicInformationContext);

    if (!value) {
        throw new Error(
            'useEventBasicInformationContext must be used within EventBasicInformationContext',
        );
    }

    return value;
};

export const useEventDispatchContext = () => {
    const dispatch = useContext(EventDispatchContext);

    if (!dispatch) {
        throw new Error(
            'useEventDispatchContext must be used within EventDispatchContext',
        );
    }

    return dispatch;
};

export const EventBasicInformationProvider: React.FC<{
    value: EventBasicInformation;
}> = ({ children, value }) => {
    const [state, dispatch] = useReducer(
        eventReducer,
        getEventBasicInfo(value),
    );

    return (
        <EventBasicInformationContext.Provider value={state}>
            <EventDispatchContext.Provider value={dispatch}>
                {children}
            </EventDispatchContext.Provider>
        </EventBasicInformationContext.Provider>
    );
};

export const EventBasicInformationContext =
    createContext<EventBasicInformation | null>(null);

export const EventDispatchContext = createContext<Dispatch<EventAction> | null>(
    null,
);
